"use client";

import clsx from "clsx";
import { Smartphone } from "lucide-react";

/**
 * A miniature mobile-frame preview shown at the bottom of every exercise
 * builder modal. Renders the question + first answer / first row in a
 * 320×480 phone-shaped frame so admins can sanity-check what learners see.
 *
 * Purely visual — does not execute any of the exercise's logic. The shape
 * adapts per `kind`:
 *   - mcq / audio_mcq   → renders the prompt + 4 choice rows
 *   - fill_blank        → renders the sentence with the blank highlighted
 *   - matching / ordering → renders rows of paired items or chips
 *   - dictation         → renders an audio player + a typing input
 *   - free_answer       → renders the prompt + a text area
 *   - free_writing      → renders the prompt + a tall text area
 */
export interface MobilePreviewProps {
  kind: string;
  prompt?: string;
  /** Per-kind secondary content (choices, pairs, etc.). */
  items?: unknown[];
  className?: string;
}

export function MobilePreview({ kind, prompt, items, className }: MobilePreviewProps) {
  return (
    <div className={clsx("flex items-start gap-3", className)}>
      <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-[#8E8E93]">
        <Smartphone size={11} />
        Preview
      </div>
      <div
        role="img"
        aria-label={`Mobile preview of the ${kind} exercise`}
        className="shrink-0 w-[260px] rounded-[28px] border-2 border-[#1C1C1E] bg-[#0A0A0A] p-2 shadow-[0_8px_24px_rgba(0,0,0,0.6)]"
      >
        {/* Notch */}
        <div className="mx-auto mb-2 h-4 w-20 rounded-full bg-[#1C1C1E]" />
        {/* Content area */}
        <div className="rounded-[18px] bg-[#141414] p-3 min-h-[280px] flex flex-col gap-2">
          <PreviewBody kind={kind} prompt={prompt} items={items} />
        </div>
      </div>
    </div>
  );
}

function PreviewBody({
  kind,
  prompt,
  items,
}: {
  kind: string;
  prompt?: string;
  items?: unknown[];
}) {
  const p = prompt ?? "—";
  switch (kind) {
    case "multiple_choice":
    case "audio_mcq":
      return (
        <>
          <p className="text-xs text-[#FAFAFA] leading-snug">{p}</p>
          <div className="flex flex-col gap-1.5 mt-1">
            {(items as string[] | undefined)?.slice(0, 4).map((c, i) => (
              <div
                key={i}
                className="rounded-md border border-[rgba(255,255,255,0.1)] bg-[#1C1C1E] px-2.5 py-1.5 text-[11px] text-[#FAFAFA]"
              >
                {c || <span className="text-[#8E8E93]">…</span>}
              </div>
            ))}
            {(!items || items.length === 0) && (
              <div className="text-[10px] font-mono text-[#8E8E93]">No choices yet</div>
            )}
          </div>
        </>
      );
    case "fill_blank":
      return (
        <p className="text-xs text-[#FAFAFA] leading-snug">
          {p.includes("_____")
            ? p.split("_____").map((part, i, arr) => (
                <span key={i}>
                  {part}
                  {i < arr.length - 1 && (
                    <span className="inline-block w-12 mx-0.5 border-b-2 border-[#FFD60A] align-baseline" />
                  )}
                </span>
              ))
            : p + " _____"}
        </p>
      );
    case "matching":
      return (
        <div className="flex flex-col gap-1.5">
          {(items as Array<{ de: string; en: string }> | undefined)?.slice(0, 4).map((pair, i) => (
            <div
              key={i}
              className="flex items-center gap-1.5 text-[10px] text-[#FAFAFA]"
            >
              <span className="flex-1 rounded bg-[#1C1C1E] border border-[rgba(255,255,255,0.08)] px-1.5 py-1 text-center">
                {pair.de || "…"}
              </span>
              <span className="text-[#8E8E93]">↔</span>
              <span className="flex-1 rounded bg-[#1C1C1E] border border-[rgba(255,255,255,0.08)] px-1.5 py-1 text-center">
                {pair.en || "…"}
              </span>
            </div>
          ))}
        </div>
      );
    case "dictation":
      return (
        <>
          <p className="text-xs text-[#FAFAFA] leading-snug">{p}</p>
          <div className="rounded-md bg-[#1C1C1E] px-2 py-1 text-[10px] font-mono text-[#8E8E93] flex items-center gap-1">
            ▶ audio (3 listens)
          </div>
          <div className="rounded-md border border-[rgba(255,255,255,0.1)] bg-[#1C1C1E] px-2 py-1.5 text-[11px] text-[#8E8E93]">
            Type what you hear…
          </div>
        </>
      );
    case "ordering":
      return (
        <div className="flex flex-col gap-1">
          <p className="text-xs text-[#FAFAFA] mb-1">{p}</p>
          <div className="flex flex-wrap gap-1">
            {(items as string[] | undefined)?.slice(0, 6).map((t, i) => (
              <span
                key={i}
                className="rounded bg-[#1C1C1E] border border-[rgba(255,255,255,0.1)] px-1.5 py-0.5 text-[10px] font-mono text-[#FAFAFA]"
              >
                {t || "…"}
              </span>
            ))}
          </div>
        </div>
      );
    case "free_answer":
      return (
        <>
          <p className="text-xs text-[#FAFAFA] leading-snug">{p}</p>
          <div className="rounded-md border border-[rgba(255,255,255,0.1)] bg-[#1C1C1E] p-2 min-h-[80px] text-[10px] text-[#8E8E93]">
            AI-graded free response…
          </div>
        </>
      );
    case "free_writing":
      return (
        <>
          <p className="text-xs text-[#FAFAFA] leading-snug">{p}</p>
          <div className="rounded-md border border-[rgba(255,255,255,0.1)] bg-[#1C1C1E] p-2 min-h-[120px] text-[10px] text-[#8E8E93]">
            Write 80–120 words… (AI-graded)
          </div>
        </>
      );
    default:
      return <p className="text-xs text-[#FAFAFA]">{p}</p>;
  }
}